import React from 'react';
import { Box, IconButton, TextField } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';

const BRACKET_PLACEHOLDER_RE = /\[[^\]]+\]/;
import { FieldEntryDTO } from '../../magic-report.type';
import { BookmarkToggle } from '../BookmarkToggle';

interface TextArrayFieldRendererProps {
  entries: FieldEntryDTO[];
  onChange: (entries: FieldEntryDTO[]) => void;
  fieldName: string;
  showAddButton?: boolean; // Control whether to show the add button at bottom
  onAddEntry?: () => void; // Expose add handler for external use
  getNextActionItemOrder?: () => number; // Optional: assign ordering when bookmarking entries
  showBookmark?: boolean; // Whether to show bookmark toggle on entries (defaults to true)
}

export const TextArrayFieldRenderer: React.FC<TextArrayFieldRendererProps> = ({
  entries,
  onChange,
  fieldName,
  showAddButton = true,
  onAddEntry,
  getNextActionItemOrder,
  showBookmark = true,
}) => {
  const handleAddEntry = () => {
    const newEntry: FieldEntryDTO = {
      value: '',
      addToActionItems: false,
      order: entries.length,
    };
    onChange([...entries, newEntry]);
  };

  // Use external handler if provided, otherwise use internal
  const addEntry = onAddEntry || handleAddEntry;

  const handleUpdateEntry = (index: number, updates: Partial<FieldEntryDTO>) => {
    const updated = entries.map((entry, i) => (i === index ? { ...entry, ...updates } : entry));
    onChange(updated);
  };

  const handleDeleteEntry = (index: number) => {
    const updated = entries.filter((_, i) => i !== index).map((entry, i) => ({ ...entry, order: i }));
    onChange(updated);
  };

  const handleToggleActionItem = (index: number) => {
    const nextValue = !entries[index].addToActionItems;
    if (nextValue) {
      const nextOrder = getNextActionItemOrder?.();
      handleUpdateEntry(index, { addToActionItems: true, actionItemOrder: nextOrder ?? entries[index].actionItemOrder });
    } else {
      handleUpdateEntry(index, { addToActionItems: false, actionItemOrder: undefined });
    }
  };

  return (
    <Box>
      {entries.map((entry, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            mb: 2,
          }}
        >
          <Box
            sx={{
              flexGrow: 1,
            }}
          >
            <TextField
              fullWidth
              multiline
              minRows={2}
              value={entry.value || ''}
              onChange={e => handleUpdateEntry(index, { value: e.target.value })}
              placeholder='Enter text'
              variant='outlined'
              error={BRACKET_PLACEHOLDER_RE.test(entry.value || '')}
              helperText={
                BRACKET_PLACEHOLDER_RE.test(entry.value || '')
                  ? 'Contains unfilled placeholder — replace [bracketed] text before finalizing.'
                  : undefined
              }
              sx={
                BRACKET_PLACEHOLDER_RE.test(entry.value || '')
                  ? {
                      '& .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
                      '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
                    }
                  : undefined
              }
            />
          </Box>

          <IconButton size='small' onClick={() => handleDeleteEntry(index)} sx={{ color: 'error.main', pt: 0.75, flexShrink: 0 }}>
            <DeleteIcon fontSize='small' />
          </IconButton>

          {showBookmark ? (
            <BookmarkToggle addToActionItems={entry.addToActionItems} onToggle={() => handleToggleActionItem(index)} sx={{ pt: 0.75 }} />
          ) : (
            <Box sx={{ width: 24, flexShrink: 0, pt: 0.75 }} />
          )}
        </Box>
      ))}

      {showAddButton && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <IconButton size='small' onClick={addEntry} sx={{ border: '1px dashed', borderColor: 'divider' }}>
            <AddIcon />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};
